// 产地证(원산지증명서) — 箱单发票 모델(buildInvoiceModel)에서 품목을 모아 원산지증명 초안 엑셀을 만든다.
// 순수 함수 모듈(브라우저·node 공용). 화면은 InvoiceGenerator.vue.
//
// 종류는 셋(2026-10 YZY): 区分符号 FCN1 = 韩中产地证(한ㆍ중국 FTA), E1 = APTA产地证(아시아ㆍ태평양 협정),
// FRCCN1 = RCEP产地证. 출고리스트엔 종류가 없어서 화면에서 HS코드별로 고른다.
// 한 장에 품목 최대 60개 — 넘으면 같은 종류를 여러 파일(-1, -2 …)로 나눈다.
// 양식은 창고가 실제로 쓰는 샘플 셀 위치 그대로다. RCEP 샘플은 .doc 인데 같은 칸 구성의 xlsx 로 만든다.
import * as XLSX from 'xlsx'
import { Styles, Sheet, packXlsx, isLooseId, boxCode } from './invoiceXlsx'

// 화면 표기는 이 label 하나만 바꾸면 된다(2026-10-04 YZY 표기 요청 — 괄호 안 코드는 확인 중). 내부 코드값(code)은 그대로.
export const COO_TYPES = [
  { code: 'FCN1', label: '韩中FTA产地证(FCN1)', short: 'KR-CN FTA', color: '#1d4ed8', bg: '#dbeafe' },
  { code: 'E1', label: 'APTA产地证(E1)', short: 'APTA', color: '#047857', bg: '#d1fae5' },
  { code: 'FRCCN1', label: 'RCEP产地证(FRCCN1)', short: 'RCEP', color: '#b45309', bg: '#fef3c7' }
]
export const COO_MAX_ITEMS = 60
// 箱单发票 총금액(USD)이 이 값 미만이면 产地证 을 만들지 않는다(2026-10-07 YZY). 기준을 바꿀 때는 여기 한 곳만.
export const COO_MIN_USD = 230
export const COO_NOT_NEEDED_TEXT = '无需做产地证'
// 「无需做产地证」도 产地证 种类 드롭다운의 4번째 선택지(2026-10-08 YZY). 코드값 NONE = 产地证 안 만듦(COO_TYPES 에는 넣지 않는다 — splitCooFiles 가 이 목록으로 파일을 나눈다).
// 세트 금액이 COO_MIN_USD 미만이면 기본값이 NONE(옛 동작 그대로), 이상이면 FCN1. color·bg = 화면 구분용 색
export const COO_NONE_CODE = 'NONE'
export const COO_CHOICES = COO_TYPES.concat([{ code: COO_NONE_CODE, label: COO_NOT_NEEDED_TEXT, short: '-', color: '#b91c1c', bg: '#fee2e2' }])
export const cooChoice = code => COO_CHOICES.find(t => t.code === code) || null

// 箱单发票 총금액(USD) = 품목 Amount 합, 센트 반올림 — 서류 기록 summary.amount·箱单发票 G열 합계와 같은 값
export function invoiceAmount(model) {
  let sum = 0
  for (const p of (model && model.pallets) || []) for (const it of p.items || []) sum += Number(it.amount) || 0
  return Math.round(sum * 100) / 100
}
export function cooNeeded(amount) {
  return Number(amount) >= COO_MIN_USD
}

function text(v) {
  if (v === null || v === undefined) return ''
  return String(v).replace(/\r/g, '').trim()
}

// 「4202.39-0000」 → 「420239」(6자리)
export function hs6(hs) {
  return text(hs).replace(/\D/g, '').slice(0, 6)
}

/**
 * 고른 파렛트의 품목을 (영어품명, HS 6자리) 로 합친다.
 * 순서 = 같은 세트 箱单发票 품목 표(model.pallets × items)에 처음 나온 순서(2026-10-06 YZY) — 합쳐진 품목은 첫 등장 위치.
 * @returns {Array<{desc, hs, qty}>}
 */
// 합치기 키(2026-10-09 YZY) — 품명은 공백·대소문자 차이를 무시, HS 는 6자리. 단가·Amount(申报金额)는 키에 넣지 않는다(产地证 서류엔 금액 칸이 없다).
export function cooKey(desc, hs) {
  return text(desc).replace(/\s+/g, ' ').toLowerCase() + '|' + hs6(hs)
}
export function cooItems(model) {
  const map = new Map()
  for (const p of (model && model.pallets) || []) {
    for (const it of p.items) {
      const hs = hs6(it.hs)
      const key = cooKey(it.desc, hs)
      if (!map.has(key)) map.set(key, { desc: it.desc, hs, qty: 0, rows: 0 })
      const m = map.get(key)
      m.qty += it.qty
      m.rows += 1
    }
  }
  return [...map.values()]
}

/**
 * 종류별로 묶고 60개씩 자른다. typeOf(hs6) → 'FCN1' | 'E1' | 'FRCCN1'
 * @returns {Array<{type, no, total, items}>}  no = 같은 종류 안 일련번호(1부터)
 */
export function splitCooFiles(items, typeOf) {
  const out = []
  COO_TYPES.forEach(t => {
    const list = items.filter(it => (typeOf(it.hs) || 'FCN1') === t.code)
    const total = Math.ceil(list.length / COO_MAX_ITEMS)
    for (let i = 0; i < total; i++) {
      out.push({ type: t.code, no: i + 1, total, items: list.slice(i * COO_MAX_ITEMS, (i + 1) * COO_MAX_ITEMS) })
    }
  })
  // seq·count = 세트 전체(종류 무관) 몇 번째 장 / 몇 장 — 하단 TOTAL 포장 수를 장마다 나눠 쓴다(2026-10-07 YZY)
  out.forEach((f, i) => { f.seq = i + 1; f.count = out.length })
  return out
}

// ───────────────────────── 표기 ─────────────────────────

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
// 「2026-09-30」 → 「SEP.30.2026」
export function cooDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text(ymd))
  if (!m) return text(ymd)
  return MONTHS[+m[2] - 1] + '.' + m[3] + '.' + m[1]
}

const ONES = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE',
  'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN']
const TENS = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY']
export function numberWords(n) {
  n = Math.floor(n)
  if (n < 20) return ONES[n]
  if (n < 100) return TENS[Math.floor(n / 10)] + (n % 10 ? '-' + ONES[n % 10] : '')
  if (n < 1000) return ONES[Math.floor(n / 100)] + ' HUNDRED' + (n % 100 ? ' AND ' + numberWords(n % 100) : '')
  return String(n)
}
// 「TOTAL:NINE(9)PALLETS ONLY.」 — 박스 출고는 CARTONS, 섞이면 둘 다
// 产地证 이 여러 장(seq 번째 / count 장)이면 托盘 수를 장마다 나눈다 — 앞장부터 올림 몫, 마지막 장이 나머지(22托/3장 → 8·8·6).
// 散货(CARTONS) 는 나누지 않고 마지막 장에만 더한다. 한 장이면 전체 그대로(2026-10-07 YZY)
export function totalPackagesText(pallets, seq, count) {
  let plt = pallets.filter(p => p.kind === 'PLT').reduce((s, p) => s + p.count, 0)
  let box = pallets.filter(p => p.kind === 'BOX').reduce((s, p) => s + p.count, 0)
  if (count > 1 && seq >= 1 && seq <= count) {
    const q = Math.ceil(plt / count)
    plt = Math.max(0, Math.min(q, plt - q * (seq - 1)))
    if (seq < count) box = 0
  }
  const part = (n, one, many) => numberWords(n) + '(' + n + ')' + (n === 1 ? one : many)
  const parts = []
  if (plt) parts.push(part(plt, 'PALLET', 'PALLETS'))
  if (box) parts.push(part(box, 'CARTON', 'CARTONS'))
  return 'TOTAL:' + (parts.join(' AND ') || '0') + ' ONLY.'
}

// 송장번호 칸 — 「RUBY0930-P01~0930-41」(마지막 번호는 화주 영문 머리를 뗀다). marks 는 머리를 안 뗀 꼴
export function palletRange(pallets, full) {
  const ids = pallets.map(p => p.id)
  if (!ids.length) return ''
  if (ids.length === 1) return ids[0]
  const last = ids[ids.length - 1]
  return ids[0] + '~' + (full ? last : last.replace(/^[A-Za-z]+/, ''))
}

// 散货 箱号 — 箱单发票 과 같은 정규화(boxCode: 개행·공백 정리, 한 칸에 여럿이면 「, 」). 옛 기록도 여기서 다시 거른다
function looseCodes(pallets) {
  const out = []
  pallets.filter(p => p.kind === 'BOX' || isLooseId(p.id)).forEach(p => {
    (p.boxCodes || []).forEach(c => boxCode(c).split(', ').filter(Boolean).forEach(x => { if (!out.includes(x)) out.push(x) }))
  })
  return out
}
// 箱号 「FK092130」 → 머리글자·번호. 꼴이 다르면 null(범위로 묶지 않는다)
function codeKey(c) {
  // 화주 라벨이 붙은 꼴(Z-donjoFG1019101)·「-1」 꼬리(…1019114-1)도 범위로 묶는다(2026-10-21 吴亚欣 #97). 머리는 한 글자(A101)부터
  const m = /^([A-Za-z](?:[A-Za-z0-9-]*?[A-Za-z])?)(\d+)(?:-(\d+))?$/.exec(c)
  return m ? { head: m[1].toUpperCase(), digits: m[2].length, n: parseInt(m[2], 10), sub: m[3] ? parseInt(m[3], 10) : 0 } : null
}
// 箱号 목록 → 연속 구간 범위들. 「FK092130~FK092139」, 끊기면 「, 」 로 여러 개(2026-10-07 YZY)
export function boxCodeRanges(codes) {
  const list = [...new Set(codes || [])].sort((a, b) => {
    const x = codeKey(a)
    const y = codeKey(b)
    if (!x || !y) return a.localeCompare(b)
    return x.head.localeCompare(y.head) || x.digits - y.digits || x.n - y.n || x.sub - y.sub
  })
  const runs = []
  list.forEach(c => {
    const k = codeKey(c)
    const run = runs[runs.length - 1]
    if (run && k && run.k && run.k.head === k.head && run.k.digits === k.digits && (run.k.n + 1 === k.n || (run.k.n === k.n && k.sub > run.k.sub))) {
      run.last = c
      run.k = k
    } else runs.push({ first: c, last: c, k })
  })
  return runs.map(r => (r.first === r.last ? r.first : r.first + '~' + r.last)).join(', ')
}
// 产地证 11번 칸(송장번호)·唛头 의 托盘/箱号 범위(2026-10-07 YZY)
//   전부 托盘 → 기존 그대로(palletRange) / 전부 散货 → 箱号 범위들 / 섞임 → 「첫 托盘号~마지막 箱号」(DEAR0930-01~FU092825)
export function cooMarkRange(pallets, full) {
  const ps = pallets || []
  const plts = ps.filter(p => !(p.kind === 'BOX' || isLooseId(p.id)))
  const codes = looseCodes(ps)
  if (!codes.length) return palletRange(plts.length ? plts : ps, full)
  if (!plts.length) return boxCodeRanges(codes)
  const sorted = boxCodeRanges(codes).split(', ')
  const last = sorted[sorted.length - 1].split('~').pop()
  return plts[0].id + '~' + last
}

function lines(...xs) {
  return xs.map(text).filter(Boolean).join('\n')
}
// 수입자 칸 — 샘플마다 꼴이 조금 다르다. FCN1 은 주소 줄을 나누고, APTA·RCEP 은 주소를 한 줄로 잇는다
function consigneeLines(im, joinAddr, mail, tel) {
  const addr = [text(im.addr1), text(im.addr2)].filter(Boolean)
  return [text(im.name)].concat(joinAddr ? [addr.join(' ')] : addr,
    im.email ? [mail + text(im.email)] : [], im.tel ? [tel + text(im.tel)] : []).filter(Boolean)
}
function exporterText(ex) {
  return lines(ex.name, [text(ex.addr1), text(ex.addr2)].filter(Boolean).join(' '))
}
function hsValue(hs) {
  // 샘플은 숫자칸. 0 으로 시작하는 HS 는 숫자로 쓰면 0 이 사라져 글자로 둔다
  return /^[1-9]\d{5}$/.test(hs) ? Number(hs) : hs
}
function oneSpace(s) {
  return text(s).replace(/\s+/g, ' ')
}

// ───────────────────────── 양식 ─────────────────────────

const TNR = 'Times New Roman'
const T = (sz, extra) => Object.assign({ font: { name: TNR, sz: sz || 12 }, v: 'center' }, extra || {})

// FCN1 — 韩中产地证(샘플 coo xlsx: A~M 열, 품목 30~89행 = 60칸, 합계 92행)
function buildFCN1(file, ctx) {
  const sh = new Sheet('Sheet1')
  ;[['A', 6.75], ['B', 1.25], ['C', 7.87], ['F', 5.5], ['G', 11.5], ['H', 9.5], ['I', 7.87], ['J', 3.25], ['K', 8.75],
    ['L', 11.75], ['M', 10.13], ['N', 9]].forEach(([c, w]) => sh.width(c, w))
  sh.height(1, 25)
  ;[3, 4, 5, 16, 18].forEach(r => sh.height(r, r === 18 ? 13.5 : 14.25))
  sh.height(6, 9.95).height(11, 14.1).height(26, 3.75).height(93, 7.5).height(102, 18).height(106, 14)
  ;[13, 14, 15].forEach(r => sh.height(r, 29))
  for (let r = 20; r <= 25; r++) sh.height(r, 13.5)
  for (let r = 30; r <= 90; r++) sh.height(r, 16)
  ;[94, 95, 96].forEach(r => sh.height(r, 11.25))

  sh.put('A1', '1ST ', T(20, { font: { name: TNR, sz: 20, b: true } }))
  sh.put('A2', "1.Exporter's name and address;", T(12))
  sh.put('A3', ctx.exporter, T(10, { wrap: true })).merge('A3:G6')
  sh.put('H2', 'Certificate No;      ', T(12))
  sh.put('I4', 'CERTIFICATE OF ORIGIN', T(14))
  sh.put('I5', '       Form for China-Korea  FTA', T(12, { font: { name: TNR, sz: 12, b: true } }))
  sh.put('A7', "2.Producer's name and address,if known", T(12))
  sh.put('A8', ctx.producer, T(10, { wrap: true })).merge('A8:G11')
  sh.put('I8', "Issued in the People's Republic of  China", T(12))
  sh.put('K9', '(see Overleaf Instruction)', T(12))
  sh.put('A12', "3.Consignee's name and address ", T(12))
  sh.put('H12', 'For Official Use Only', T(12))
  sh.put('A13', consigneeLines(ctx.importer, false, 'EMAIL:', 'TEL:').join('\n'), T(10, { wrap: true })).merge('A13:G15')
  sh.put('A16', '4.Means of transport and route (as far ad knowe)', T(12))
  sh.put('H17', '5.Remarks:', T(12))
  sh.put('A18', 'Departure date:', T(12)).put('A19', ctx.date, T(10))
  sh.put('A20', 'Vessel/Flight/Train/Vehicle No', T(12)).put('A21', ctx.vessel, T(10))
  sh.put('A22', 'Port of loading:', T(12)).put('A23', ctx.portLoading, T(10))
  sh.put('A24', 'Port of discharge;', T(12)).put('A25', ctx.portDischarge, T(10))

  const head = [
    ['A27', '6.Item'], ['B27', '7.Number and kind of packages;description'], ['G27', '8.HS code (six'], ['H27', '9.Origin'],
    ['I27', '10.Gross weight,quantity'], ['L27', '11.Number and'], ['M27', '12.Invoiced'],
    ['A28', 'number'], ['B28', 'of goods'], ['G28', 'digie code)'], ['H28', 'criterion'], ['I28', '(Quantity Unit) or ither'],
    ['L28', 'date of invoice'], ['M28', 'value'], ['A29', 'Max 20'], ['I29', 'measures (liters,㎡, etc.)']
  ]
  head.forEach(([a, v]) => sh.put(a, v, T(12)))

  file.items.forEach((it, i) => {
    const r = 30 + i
    sh.put('B' + r, it.desc, T(12))
    sh.put('G' + r, hsValue(it.hs), T(10, { h: 'left' }))
    sh.put('I' + r, it.qty + 'PIECES', T(10, { wrap: true }))
  })
  sh.put('L30', ctx.invoiceNo, T(10, { h: 'left', wrap: true })).merge('L30:M30')
  sh.put('L31', ctx.date, T(10))
  sh.put('B92', ctx.totalText, { font: { name: 'Arial', sz: 11 }, v: 'center' })

  sh.put('A94', '13.Declaration by the exporter:', T(10)).put('H94', '14.Certification:', T(12))
  sh.put('A95', 'The undersigened hereby declares that the above detaile and statement', T(10))
  sh.put('A96', 'are correct,that all the goods were produced in', T(10))
  sh.put('H95', 'On the basis of control carried out,it is hereby certified that the', T(12))
  sh.put('H96', 'information herein is correct and that the goods described comply', T(12))
  sh.put('H97', 'with the origin requirements specified in the China - Korea FTA.', T(12))
  sh.put('D98', 'CHINA', T(12)).put('D99', '(Country)', T(10))
  sh.put('A100', ' and that they comply with the origin requirements specified in the ', T(12))
  sh.put('A101', ' FTA for the goods exported to ', T(12))
  sh.put('D102', 'KOREA', T(12))
  sh.put('C103', '            (Importing Country)', T(10))
  sh.put('A107', 'Place and date,signature of authorized signatory ', T(10))
  sh.put('H107', 'Place and date,signature of authorized body', T(10))

  // 테두리 — 샘플 칸 구획
  ;['A2:G6', 'A7:G11', 'A12:G15', 'A16:G26', 'H2:M11', 'H12:M16', 'H17:M26',
    'A27:A93', 'B27:F93', 'G27:G93', 'H27:H93', 'I27:K93', 'L27:L93', 'M27:M93',
    'A94:G98', 'A99:G102', 'A103:G106', 'H94:M106', 'A107:G107', 'H107:M107'].forEach(r => sh.box(r))
  sh.box('A13:G15')
  return sh
}

// E1 — APTA产地证(샘플 xls 「韩国FA」 시트: 인쇄 양식 위에 찍는 값 자리. 품목 23행부터)
function buildE1(file, ctx) {
  const sh = new Sheet('韩国FA')
  ;[['A', 8.63], ['B', 30.13], ['H', 9.25], ['I', 26.75]].forEach(([c, w]) => sh.width(c, w))
  const V = T(10)
  sh.put('A2', '1.Goods consigned from (Exporter’s business name,address, country)', V)
  const ex = ctx.exporter.split('\n')
  sh.put('A5', ex[0] || '', V).put('A6', ex.slice(1).join(' '), V)
  sh.put('H8', 'KOREA FORM B', V)
  sh.put('A9', '2.Consignee', V)
  consigneeLines(ctx.importer, true, 'E-MAIL:', 'TEL: ').slice(0, 4).forEach((t, i) => sh.put('A' + (10 + i), t, V))
  sh.merge('A3:F3')
  sh.put('A15', '3.Means of transport and route (as far as known)', V)
  sh.put('A17', 'FROM ' + ctx.portLoading + ' TO ' + ctx.portDischarge + '  BY SEA', V)
  sh.put('A21', '4.H.S.Code', V).put('B21', '6.Marks and numbers', V)
  sh.put('C21', '7.Number and kind of packages:description of ', V)
  sh.put('H21', '9.Gross,weight,or other,quantity', V).put('I21', '10.Number and date of invoices', V)
  sh.put('I22', ctx.invoiceNo, V)
  sh.put('B23', ctx.marks, Object.assign({}, V, { wrap: true }))
  sh.put('I23', ctx.date, V)
  file.items.forEach((it, i) => {
    const r = 23 + i
    if (i < 2) sh.height(r, 49)
    sh.put('A' + r, hsValue(it.hs), Object.assign({}, V, { h: 'left' }))
    sh.put('C' + r, it.desc, V)
    sh.put('H' + r, it.qty + 'PIECES', V)
  })
  // 샘플: 품목 2줄일 때 합계 30행, 수입국 37행 — 품목이 많으면 그만큼 내려간다
  const totalRow = Math.max(30, 23 + file.items.length + 1)
  sh.put('C' + totalRow, ctx.totalText, V)
  const destRow = totalRow + 7
  sh.put('B' + destRow, 'KOREA', V).merge('B' + destRow + ':D' + destRow)
  // 테두리 — 샘플(FB_ANSWER xls 「韩国FA」, 2026-10 吴亚欣 #165) 칸 구획: 머리 A:F|G:I, 품목표 열별 세로선, 꼬리 A:F|G:I. 모두 얇은 선
  const bodyEnd = totalRow + 5
  ;['A2:F7', 'A8:F14', 'G2:I14', 'A15:F20', 'G15:I20', 'A' + (bodyEnd + 1) + ':F' + (destRow + 3), 'G' + (bodyEnd + 1) + ':I' + (destRow + 3)].forEach(r => sh.box(r))
  ;['A', 'B', 'C:F', 'G', 'H', 'I'].forEach(c => {
    const [a, b] = c.split(':')
    sh.box(a + '21:' + (b || a) + '21').box(a + '22:' + (b || a) + bodyEnd)
  })
  return sh
}

// FRCCN1 — RCEP产地证(샘플은 .doc 표. 같은 칸 구성의 xlsx — 1~5 머리, 6~13 품목표, 14~17 꼬리)
function buildFRCCN1(file, ctx) {
  const sh = new Sheet('RCEP')
  ;[['A', 6], ['B', 9], ['C', 30], ['D', 10], ['E', 10], ['F', 10], ['G', 14], ['H', 18]].forEach(([c, w]) => sh.width(c, w))
  const L = T(10, { wrap: true, v: 'top' })
  sh.put('A1', '1. Goods Consigned from (Exporter’s name, address and country)', L).merge('A1:D1')
  sh.put('A2', ctx.exporter, L).merge('A2:D2')
  sh.put('E1', 'Certificate No.\nForm RCEP\nREGIONAL COMPREHENSIVE ECONOMIC PARTNERSHIP AGREEMENT\nCERTIFICATE OF ORIGIN\n\nIssued in CHINA\n(Country)', T(10, { wrap: true, v: 'top', h: 'center' })).merge('E1:H4')
  sh.put('A3', '2. Goods Consigned to (Importer’s/ Consignee’s name, address, country)', L).merge('A3:D3')
  sh.put('A4', consigneeLines(ctx.importer, true, 'E-MAIL: ', 'TEL: ').join('\n'), L).merge('A4:D4')
  sh.put('A5', '3. Producer’s name, address and country (if known)', L).merge('A5:D5')
  sh.put('A6', ctx.producer, L).merge('A6:D6')
  sh.put('E5', '5. For Official Use\nPreferential Treatment:\n( Given              ( Not Given (Please state reason/s)\n\n………………………………………\nSignature of Authorised Signatory of the Customs Authority of the Importing Country', L).merge('E5:H7')
  sh.put('A7', '4. Means of transport and route (if known)\nDeparture Date: ' + ctx.date + '\nVessel’s name/Aircraft flight number, etc.:\n' + ctx.vessel +
    '\nPort of Discharge: ' + ctx.portDischarge, L).merge('A7:D7')
  ;[[1, 30], [2, 45], [3, 15], [4, 75], [5, 15], [6, 60], [7, 100]].forEach(([r, h]) => sh.height(r, h))

  const head = ['6. Item number', '7. Marks and numbers on packages', '8. Number and kind of packages; and description of goods.',
    '9. HS Code of the goods (6 digit-level)', '10. Origin Conferring Criterion', '11. RCEP Country of Origin',
    '12. Quantity (Gross weight or other measurement), and value (FOB) where RVC is applied', '13. Invoice number(s) and date of invoice(s)']
  head.forEach((v, i) => sh.put(String.fromCharCode(65 + i) + '8', v, T(9, { wrap: true, v: 'top', h: 'center' })))
  sh.height(8, 90)
  sh.grid('A8:H8')
  file.items.forEach((it, i) => {
    const r = 9 + i
    sh.put('A' + r, i + 1, T(10, { h: 'center' }))
    sh.put('C' + r, it.desc, T(10, { wrap: true }))
    sh.put('D' + r, hsValue(it.hs), T(10, { h: 'left' }))
    sh.put('F' + r, 'CHINA', T(10))
    sh.put('G' + r, it.qty + 'PIECES', T(10))
  })
  sh.put('H9', ctx.invoiceNo + '\n' + ctx.date, T(10, { wrap: true, v: 'top' }))
  const last = 9 + Math.max(file.items.length, 1)
  sh.put('C' + (last + 1), ctx.totalText, T(10))
  const end = last + 2
  ;['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].forEach(c => sh.box(c + '9:' + c + end))
  const r14 = end + 1
  sh.put('A' + r14, '14. Remarks', L).merge('A' + r14 + ':H' + r14).box('A' + r14 + ':H' + r14)
  const r15 = r14 + 1
  sh.height(r15, 150)
  sh.put('A' + r15, '15. Declaration by the exporter or producer\n\nThe undersigned hereby declares that the above details and statements are correct and that the goods covered in this Certificate comply with the requirements specified for these goods in the Regional Comprehensive Economic Partnership Agreement. These goods are exported to:\n\nKOREA\n(importing country)\n\n\nPlace and date and signature of authorised signatory', L).merge('A' + r15 + ':D' + r15)
  sh.put('E' + r15, '16. Certification\n\nOn the basis of control carried out, it is hereby certified that the information herein is correct and that the goods described comply with the origin requirements specified in the Regional Comprehensive Economic Partnership Agreement.\n\n\n\nPlace and date, signature and seal or stamp of Issuing Body', L).merge('E' + r15 + ':H' + r15)
  const r17 = r15 + 1
  sh.put('A' + r17, '17. ( Back-to-back Certificate of Origin     ( Third-party invoicing     ( ISSUED RETROACTIVELY', L).merge('A' + r17 + ':H' + r17)
  ;['A1:D2', 'A3:D4', 'A5:D6', 'A7:D7', 'E1:H4', 'E5:H7', 'A' + r15 + ':D' + r15, 'E' + r15 + ':H' + r15, 'A' + r17 + ':H' + r17].forEach(r => sh.box(r))
  return sh
}

const BUILDERS = { FCN1: buildFCN1, E1: buildE1, FRCCN1: buildFRCCN1 }

// 송장번호 칸 — FCN1·RCEP 샘플은 끝에 파일 일련번호(-1, -2)를 붙였다. APTA 샘플은 안 붙였다 — 여러 장일 때만 붙인다
export function cooInvoiceNo(file, model) {
  const suffix = file.type !== 'E1' || file.total > 1 ? '-' + file.no : ''
  return cooMarkRange(model.pallets) + suffix
}

/**
 * 원산지증명 엑셀 한 파일(Uint8Array).
 * @param {{type,no,total,items}} file  splitCooFiles 의 한 덩어리
 * @param {object} model  buildInvoiceModel 결과(파렛트 범위·포장 수에 쓴다)
 * @param {object} info   { date, exporter:{name,addr1,addr2}, importer:{...}, portLoading, finalDest, carrier }
 * @param {object} coo    { exporter: 1번 칸 글(여러 줄) — 비면 수출자, vessel: 선명/항차 — 비면 carrier }
 */
export function writeCooXlsx(file, model, info, coo) {
  const c = coo || {}
  const ctx = {
    exporter: text(c.exporter) || exporterText(info.exporter || {}),
    producer: exporterText(info.exporter || {}),
    importer: info.importer || {},
    date: cooDate(info.date),
    vessel: text(c.vessel) || text(info.carrier),
    portLoading: oneSpace(info.portLoading),
    portDischarge: oneSpace(info.finalDest),
    invoiceNo: cooInvoiceNo(file, model),
    marks: cooMarkRange(model.pallets, true),
    totalText: totalPackagesText(model.pallets, file.seq, file.count)
  }
  const styles = new Styles()
  const sh = BUILDERS[file.type](file, ctx)
  return packXlsx([sh], styles, 0)
}

// 파일명 — 产地证-FCN1_수입자_(화주)_MMDD-1.xlsx
export function cooFileName(file, info, model) {
  const clean = s => text(s).replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim()
  const d = text(info.date).replace(/-/g, '').slice(4)
  const co = model.companyids.join('+')
  return clean('产地证-' + file.type + '_' + ((info.importer && info.importer.name) || co) + '_(' + co + ')_' + d + '-' + file.no) + '.xlsx'
}
// 일괄 생성 产地证 이름 — 箱单发票 과 같은 순번 이름(base = batchDocBase). 한 종류가 여러 장이면 끝에 -1, -2 …(2026-10-06 YZY)
export function batchCooFileName(file, base) {
  return '产地证-' + file.type + '_' + base + (file.total > 1 ? '-' + file.no : '') + '.xlsx'
}

/**
 * 여러 파일을 zip 하나로(Uint8Array). SheetJS 내장 CFB 를 쓰는데 zip 안 파일명에 한글·한자를 못 쓴다(깨진다)
 * → zip 안 이름은 영숫자로만 만든다. zip 파일 자체 이름은 화면에서 한자로 준다.
 */
export function zipFiles(list) {
  const cfb = XLSX.CFB.utils.cfb_new()
  const used = new Set()
  list.forEach(f => {
    let name = 'COO' + f.name.replace(/[^\x20-\x7E]+/g, '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim()
    while (used.has(name)) name = name.replace(/(\.xlsx)?$/, '_.xlsx')
    used.add(name)
    XLSX.CFB.utils.cfb_add(cfb, '/' + name, f.data)
  })
  const out = XLSX.CFB.write(cfb, { fileType: 'zip', type: 'array', compression: false })
  return out instanceof Uint8Array ? out : new Uint8Array(out)
}

// zip(무압축, 파일 이름 UTF-8) — 일괄 생성·화주별 출고리스트처럼 zip 안 이름에 한글·한자가 필요할 때(2026-10-06 YZY).
// SheetJS CFB 는 이름을 글자당 1바이트로 잘라 써서 한글이 깨진다 → 직접 쓴다. xlsx 는 이미 압축돼 있어 무압축이면 충분하다.
// 같은 이름이 또 오면 「 (2)」 를 붙인다
let CRC_TABLE = null
function crc32(u8) {
  if (!CRC_TABLE) {
    CRC_TABLE = new Uint32Array(256)
    for (let n = 0; n < 256; n++) {
      let c = n
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1
      CRC_TABLE[n] = c >>> 0
    }
  }
  let c = 0xFFFFFFFF
  for (let i = 0; i < u8.length; i++) c = CRC_TABLE[(c ^ u8[i]) & 0xFF] ^ (c >>> 8)
  return (c ^ 0xFFFFFFFF) >>> 0
}
export function zipFilesUtf8(list) {
  const enc = new TextEncoder()
  const used = new Set()
  const now = new Date()
  const dosTime = (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1)
  const dosDate = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate()
  const parts = []
  const centrals = []
  let offset = 0
  // f.dir — zip 안 폴더(箱单发票列表 날짜 일괄 다운로드가 화주·세트별로 나눌 때, 2026-10-07 YZY). 없으면 예전처럼 맨 위에
  const clean = s => String(s).replace(/[\\/:*?"<>|\r\n]+/g, ' ').trim()
  list.forEach(f => {
    const dir = f.dir ? clean(f.dir) + '/' : ''
    const base = clean(f.name)
    const dot = base.lastIndexOf('.')
    let name = dir + base
    for (let k = 2; used.has(name); k++) name = dir + (dot > 0 ? base.slice(0, dot) : base) + ' (' + k + ')' + (dot > 0 ? base.slice(dot) : '')
    used.add(name)
    const nb = enc.encode(name)
    const data = f.data instanceof Uint8Array ? f.data : new Uint8Array(f.data)
    const crc = crc32(data)
    const header = central => {
      const h = new Uint8Array((central ? 46 : 30) + nb.length)
      const v = new DataView(h.buffer)
      let p = 0
      const w16 = x => { v.setUint16(p, x, true); p += 2 }
      const w32 = x => { v.setUint32(p, x >>> 0, true); p += 4 }
      w32(central ? 0x02014b50 : 0x04034b50)
      if (central) w16(20)
      w16(20); w16(0x0800); w16(0); w16(dosTime); w16(dosDate) // 0x0800 = 이름 UTF-8, 방식 0 = 무압축
      w32(crc); w32(data.length); w32(data.length); w16(nb.length); w16(0)
      if (central) { w16(0); w16(0); w16(0); w32(0); w32(offset) }
      h.set(nb, p)
      return h
    }
    const local = header(false)
    centrals.push(header(true))
    parts.push(local, data)
    offset += local.length + data.length
  })
  const cdSize = centrals.reduce((s, c) => s + c.length, 0)
  const end = new Uint8Array(22)
  const ev = new DataView(end.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(8, centrals.length, true)
  ev.setUint16(10, centrals.length, true)
  ev.setUint32(12, cdSize, true)
  ev.setUint32(16, offset, true)
  const out = new Uint8Array(offset + cdSize + 22)
  let q = 0
  parts.concat(centrals, [end]).forEach(c => { out.set(c, q); q += c.length })
  return out
}

export default { COO_TYPES, COO_CHOICES, COO_NONE_CODE, cooChoice, COO_MAX_ITEMS, COO_MIN_USD, COO_NOT_NEEDED_TEXT, invoiceAmount, cooNeeded, cooInvoiceNo, hs6, cooKey, cooItems, splitCooFiles, cooDate, numberWords, totalPackagesText, palletRange, boxCodeRanges, cooMarkRange, writeCooXlsx, cooFileName, zipFiles, zipFilesUtf8 }
